import axios from 'axios';
import PropTypes from 'prop-types';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchCompanyNames, fetchEntityNames, fetchImages, fetchOriginalNames, usePersistedState } from '../utils/Common';

export const UserProfileContext = createContext();

export const UserProfileProvider = ({ children }) => {
  const [isImagesLoading, setIsImagesLoading] = useState(false);
  const [groupedItems, setGroupedItems] = useState([]);
  const [checkingToken, setCheckingToken] = useState(true);
  const [tokenLoaded, setTokenLoaded] = usePersistedState('tokenLoaded', false, true);
  const [addresses, setAddresses] = usePersistedState('addresses', [], tokenLoaded);
  const [phones, setPhones] = usePersistedState('phones', [], tokenLoaded);
  const [groups, setGroups] = usePersistedState('groups', [], tokenLoaded);
  const [roles, setRoles] = usePersistedState('roles', [], tokenLoaded);
  const [titles, setTitles] = usePersistedState('titles', [], tokenLoaded);
  const [emails, setEmails] = usePersistedState('emails', [], tokenLoaded);
  const [uid, setUid] = usePersistedState('uid', undefined, tokenLoaded);
  const [isLoginRequired, setIsLoginRequired] = usePersistedState('isLoginRequired', false, tokenLoaded);
  const didFetchRef = useRef(false);
  const [images, setImages] = usePersistedState('images', []);
  const [names, setNames] = usePersistedState('names', []);
  const [originalNames, setOriginalNames] = usePersistedState('originalNames', []);
  const [companyNames, setCompanyNames] = usePersistedState('companyNames', []);
  const [userProfile, setUserProfile] = usePersistedState('userProfile', {
    uid: '',
    firstName: '',
    lastName: '',
    email: '',
    commonName: '',
    title:'',
    firmId: localStorage.getItem('FIRM'),
    picture: '',
    description: '',
    telephoneNumber: '',
    street: '',
    myDomainName: '',
    groups: [],
    roles: [],
    titles:[]
  });

  useEffect(() => {
    if (!didFetchRef.current) {
      didFetchRef.current = true;
      fetchEntityNames(setNames, tokenLoaded);
      fetchOriginalNames(setOriginalNames, tokenLoaded);
      fetchCompanyNames(setCompanyNames, tokenLoaded);
      fetchImages(setImages, images, tokenLoaded, isImagesLoading, setIsImagesLoading);
    }
  }, [images, isImagesLoading, setAddresses, setCompanyNames, setEmails, setGroups, setImages, setNames, setOriginalNames, setPhones, setUid, setUserProfile, tokenLoaded,setGroupedItems,groupedItems,setRoles,setTitles]);

  useEffect(() => {
    const jwtToken = localStorage.getItem('JWT-TOKEN');
    if (jwtToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
      setTokenLoaded(true);
    }
    setCheckingToken(false);
  }, [setTokenLoaded]);

  useEffect(() => {
    if (tokenLoaded) {
      localStorage.setItem("JWT-TOKEN", localStorage.getItem("JWT-TOKEN"));
    }
  }, [tokenLoaded]);

  const clearUserProfileState = useCallback(() => {
    setNames([]);
    setOriginalNames([]);
    setCompanyNames([]);
    setTokenLoaded(false);
    localStorage.removeItem("originalNames");
    localStorage.removeItem("companyNames");
    localStorage.removeItem("userProfile");
    localStorage.removeItem("domainImages");
    localStorage.removeItem("images");
    localStorage.removeItem("groups");
    localStorage.removeItem("categoryId");
    localStorage.removeItem("SUBSCRIPTION");
    localStorage.removeItem("JWT-TOKEN");
    localStorage.removeItem('emails');
    localStorage.removeItem('addresses');
    localStorage.removeItem('phones');
    localStorage.removeItem('permissions');
    localStorage.removeItem("groups");
    localStorage.removeItem("roles");
    localStorage.removeItem("titles");
  }, [setCompanyNames, setNames, setOriginalNames, setTokenLoaded]);

  const value = useMemo(() => ({
    addresses, phones, emails, userProfile, uid, setUid, setAddresses, setPhones, setEmails, setUserProfile, isLoginRequired, setIsLoginRequired, clearUserProfileState, names, setNames, setCompanyNames, companyNames, setOriginalNames, originalNames, checkingToken, setTokenLoaded, setImages, tokenLoaded, images, groups, setGroups,groupedItems, setGroupedItems,roles,setRoles,setTitles,titles
  }), [addresses, phones, emails, userProfile, uid, setUid, setAddresses, setPhones, setEmails, setUserProfile, isLoginRequired, setIsLoginRequired, clearUserProfileState, names, setNames, setCompanyNames, companyNames, setOriginalNames, originalNames, checkingToken, setTokenLoaded, setImages, tokenLoaded, images, groups, setGroups, groupedItems,setGroupedItems,roles,setRoles,setTitles,titles]);

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

UserProfileProvider.propTypes = {
  children: PropTypes.node,
};
